import type { MyAffiliateLedger as Ledger } from "@/features/affiliates/my-code-api";
import type { CatalogProduct } from "@/features/products/catalogApi";

export type ReferralLink = Ledger["links"][number];

const QR_PREFIX = "data:image/png;base64,";

/**
 * Link giới thiệu để hiện trong hộp "Mã AFF của tôi". AFF phát cho mỗi nhân viên
 * MỘT link chung (brand / channel / campaign rỗng, không mang `productId` lẫn
 * `target`) — ưu tiên link đó; không có thì lấy link đầu KHÔNG phải link sản
 * phẩm. Link sản phẩm đang đẩy và link món tự chọn (bản 2) cũng có ba ô kia
 * rỗng nên phải xét hai ô này, và không bao giờ được đem ra làm link chung. Link phải là https: địa chỉ do hệ khác gửi
 * sang, không hiện / copy thứ không phải địa chỉ web. undefined = AFF chưa phát
 * link.
 */
export function pickReferralLink(links: ReferralLink[] | undefined): ReferralLink | undefined {
  const usable = usableLinks(links).filter((link) => !link.productId && !link.target);
  return usable.find((link) => !link.brand && !link.channel && !link.campaign) ?? usable[0];
}

/**
 * «Sản phẩm đang đẩy» (danh sách Marketing, CEO 1a · 2a 30/10/2026): AFF tự
 * tạo một link cho mỗi sản phẩm đang bật, mang `productId` + `productName`. Giữ
 * thứ tự AFF gửi; link thiếu tên thì không hiện (không biết gửi khách cho việc gì).
 */
export function productReferralLinks(links: ReferralLink[] | undefined): ReferralLink[] {
  return usableLinks(links).filter((link) => link.productId && link.productName?.trim());
}

/**
 * Link món tự chọn đã có cho (gói, concept) — bản 2, CEO 3a 30/10/2026. Concept
 * rỗng = gói không kèm concept, là món KHÁC gói đó kèm concept.
 */
export function catalogLinkFor(
  links: ReferralLink[] | undefined,
  packageCode: string,
  conceptId: string,
): ReferralLink | undefined {
  return usableLinks(links).find(
    (link) =>
      link.target?.packageCode === packageCode && (link.target.conceptId ?? "") === conceptId,
  );
}

/**
 * Thương hiệu GNPF đã có trang đặt lịch trên Cổng, theo thứ tự hiện ở ô chọn —
 * gương `affcentral.HouseOfBrand` của máy chủ (Ảnh Thẻ + Profile chung nhà gnpf,
 * Travel là gnt).
 */
export const BOOKING_BRANDS: readonly string[] = ["gnat", "gnpf", "gnca", "gntr"];

/**
 * Gói đưa vào ô «Chọn sản phẩm» (CEO 2a · 3a, 30/10/2026): đủ các thương hiệu
 * đang bán có trang đặt lịch, chỉ gói đang bán VÀ hiện trên trang đặt lịch (gói
 * chỉ bán tại quầy thì khách bấm link cũng không đặt được). Xếp theo thương hiệu
 * rồi theo thứ tự bảng giá.
 */
export function pickableProducts(
  products: readonly CatalogProduct[],
  sellingBrands: readonly string[],
): CatalogProduct[] {
  const rank = (brand: string) => BOOKING_BRANDS.indexOf(brand);
  return products
    .filter(
      (p) =>
        p.isActive &&
        !p.publicHidden &&
        rank(p.brandCode) >= 0 &&
        sellingBrands.includes(p.brandCode),
    )
    .sort((a, b) => rank(a.brandCode) - rank(b.brandCode) || a.sortOrder - b.sortOrder);
}

function usableLinks(links: ReferralLink[] | undefined): ReferralLink[] {
  return (links ?? []).filter((link) => link.url.startsWith("https://"));
}

/** Ảnh mã QR AFF gửi kèm link; chỉ nhận ảnh PNG nhúng, thứ khác coi như không có. */
export function referralQrSrc(link: ReferralLink): string | undefined {
  return link.qrPng.startsWith(QR_PREFIX) && link.qrPng.length > QR_PREFIX.length
    ? link.qrPng
    : undefined;
}
